import { useEffect, useRef } from "react";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAACjUz0An8S6vqPPD";

function safeCall(fn, arg) {
  try {
    fn?.(arg);
  } catch (e) {
    console.warn("Turnstile callback error:", e);
  }
}

const IS_LOCALHOST =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

/**
 * Cloudflare Turnstile widget (explicit render). Managed mode is configured in the Cloudflare dashboard.
 * Calls onVerify(token) when the challenge succeeds; send token as cf_turnstile_response to your API.
 * Skipped on localhost to avoid cross-origin "Script error" in dev.
 */
export function TurnstileWidget({ onVerify, onExpire, theme = "auto" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  onVerifyRef.current = onVerify;
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (IS_LOCALHOST) return;
    const container = containerRef.current;
    if (!container) return;

    function render() {
      try {
        if (!window.turnstile || !container) return;
        if (widgetIdRef.current != null) return;
        const id = window.turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          theme,
          callback: (token) => safeCall(onVerifyRef.current, token),
          "expired-callback": () => safeCall(onExpireRef.current),
        });
        widgetIdRef.current = id;
      } catch (e) {
        console.warn("Turnstile render error:", e);
      }
    }

    // Do not use turnstile.ready() - it throws when script is loaded with async/defer.
    // Just poll for window.turnstile and call render() when available.
    if (window.turnstile) {
      render();
    } else {
      const t = setInterval(() => {
        if (window.turnstile) {
          clearInterval(t);
          render();
        }
      }, 50);
      return () => {
        clearInterval(t);
        try {
          if (widgetIdRef.current != null && window.turnstile?.remove) {
            window.turnstile.remove(widgetIdRef.current);
          }
        } catch (_) {}
        widgetIdRef.current = null;
      };
    }

    return () => {
      try {
        if (widgetIdRef.current != null && window.turnstile?.remove) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch (_) {}
      widgetIdRef.current = null;
    };
  }, [theme]);

  if (IS_LOCALHOST) {
    return (
      <div className="flex justify-center my-2 py-4 text-sm text-gray-500" aria-hidden>
        Verification skipped on localhost. Use a deployed URL to test Turnstile.
      </div>
    );
  }
  return <div ref={containerRef} className="flex justify-center my-2" aria-label="Cloudflare Turnstile verification" />;
}
